import { useEffect, useState } from 'react';
import { adminApi } from '../services/api';

type VideoProvider = 'daily' | 'agora';

interface VideoProviderSettings {
  provider: VideoProvider;
  agoraConfigured: boolean;
}

export function VideoProviderPage() {
  const [settings, setSettings] = useState<VideoProviderSettings | null>(null);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi<{ settings: VideoProviderSettings }>('/admin/video-provider')
      .then((data) => setSettings(data.settings))
      .catch((err: unknown) => {
        setMessage(err instanceof Error ? err.message : 'Could not load the live-room provider.');
      });
  }, []);

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    setMessage('');
    try {
      const saved = await adminApi<{ settings: VideoProviderSettings }>('/admin/video-provider', {
        method: 'PUT',
        body: JSON.stringify({ provider: settings.provider }),
      });
      setSettings(saved.settings);
      setMessage(
        'Saved. New video and audio sessions on the website and the phone app use this provider. A session that already has a room stays on the provider it was created with.'
      );
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Could not save the live-room provider.');
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return <p className="text-sm text-gray-500">Loading live-room settings…</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Live sessions</h1>
        <p className="mt-1 max-w-2xl text-sm text-gray-600">
          One choice for every new video and audio session, on the website and the phone app. Chat
          stays chat. Clients and therapists do not pick a provider.
        </p>
      </div>

      {message ? <p className="max-w-2xl text-sm text-gray-700">{message}</p> : null}

      <form
        className="max-w-lg space-y-4 rounded-xl border border-gray-200 bg-white p-4 text-sm"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <fieldset className="space-y-3">
          <legend className="font-medium text-gray-900">Provider for new sessions</legend>
          <label className="flex items-start gap-2">
            <input
              type="radio"
              name="provider"
              className="mt-1"
              checked={settings.provider === 'daily'}
              onChange={() => setSettings({ ...settings, provider: 'daily' })}
            />
            <span>
              Daily
              <span className="mt-1 block text-xs text-gray-500">
                Current default. Sessions open in a Daily room.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2">
            <input
              type="radio"
              name="provider"
              className="mt-1"
              checked={settings.provider === 'agora'}
              onChange={() => setSettings({ ...settings, provider: 'agora' })}
            />
            <span>
              Agora
              <span className="mt-1 block text-xs text-gray-500">
                {settings.agoraConfigured
                  ? 'Server credentials are present. New sessions will use Agora.'
                  : 'Unavailable until AGORA_APP_ID and AGORA_APP_CERTIFICATE are set on the server. Saving Agora without them is rejected.'}
              </span>
            </span>
          </label>
        </fieldset>

        <button
          type="submit"
          disabled={saving}
          className="rounded bg-gray-900 px-3 py-1.5 text-white disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </form>
    </div>
  );
}
